/**
 * useTutorLiveFeedGeneration — générer une activité dans le champ du fil (tech-derisk, points 10 et 15 ;
 * registre G-01 à G-21).
 *
 * Le moteur reste le réducteur porté de `GenerateActivityModal.js` (`useGenerateActivityFlow`) : mêmes appels,
 * mêmes corps, à brouillon égal. Le champ ne fait que générer, et le dit (aucune conversation simulée) :
 *  - la cible suit le filtre : l'élève regardé, ou tout le groupe sans filtre (l'ancienne « Nouvelle activité
 *    commune », `activityTodoForStudents`, même quand la séance n'a qu'un inscrit) ;
 *  - le texte tapé devient les instructions ; classe et matière viennent de l'élève (G-01, G-02) ; le
 *    chapitre, le type, la durée, les compétences et « Personnaliser » sont des puces préremplies au-dessus du
 *    champ (G-03, G-05, G-06) : **chacune se règle sur place**, dépliée au-dessus du champ
 *    (`<TutorSessionSettingSheet inline>`, le même brouillon) ; « Personnaliser » bascule d'un toucher. Plus aucun
 *    formulaire en modale dans la salle : il ouvrait une seconde voie, où les propositions arrivaient aussi (Wajih,
 *    live en prod du 03/11 : « une expérience hybride à supprimer, une seule voie, celle du fil ») ;
 *  - un champ qui manque : sa puce le dit (« + »), rien ne part, et l'envoi déplie la puce qui manque (G-09) ;
 *    l'« Entrée » paraît avec un texte (elle mène alors au champ qui manque), un support, ou une configuration
 *    complète (round 4 : « juste configurer suffit ») ;
 *  - la classe et la matière se règlent par leurs puces : c'est là que le tuteur choisit la classe d'une activité
 *    commune ;
 *  - le trombone, le dépôt et le collage (au web, écoutés sur le champ seulement : jamais un collage destiné
 *    au chat) joignent un support (G-10, G-19, G-20) ; une image passe d'abord par le recadrage au web (G-21) ;
 *  - l'envoi : les trois `generateMaterialsSkillsPart{1,2,3}` (G-11), ou les similaires d'un support lu ou
 *    d'une activité (G-13) ; une activité au chapitre peu sûr déplie d'abord la puce du chapitre, où il se choisit
 *    (`useSourceChapterFix`, la même correction que les deux autres « Générer... ») — sans aucun chapitre pour sa
 *    matière dans la classe, l'envoi la confirme sans chapitre — « Générer une activité similaire » pose l'activité en support dans le champ, ses
 *    réglages préremplis et modifiables, et part à l'envoi du masteur (Wajih, 03/11) ; « {champion} prépare… » le temps qu'ils arrivent, puis les trois propositions en une
 *    ligne (G-12, « 1 sur 3 », côte à côte agrandie), « Autres énoncés », « Ajouter à sa liste » (G-14), le quiz de
 *    prérequis par énoncé et « Montrer à l'élève » ; toutes en échec, « Réessayer » ;
 *  - après une création en live, l'élève est prévenu (`newTodo`, F-48), comme le legacy.
 *
 * **Les maquettes du 04/11, câblées** (règles pures : `tutorLiveGenerationRules`) :
 *  - la génération qui part laisse une **ligne vivante** dans le fil (d'où elle part, les réglages repris, les
 *    propositions qui s'écrivent), puis une **carte « prêtes »** ; la **relecture se fait au centre** (`review`,
 *    `<TutorReviewStage>`), ouverte seule si le tuteur regarde encore la même vue qu'à l'envoi, sinon la carte garde
 *    une pastille et dit pendant qui elle est arrivée ; survoler une proposition en montre l'aperçu (`peek`) ;
 *  - « Ajouter et montrer » crée et montre (le geste par défaut) ; « Ajouter à la liste » crée (l'ancien « Créer
 *    l'activité ») ; la relecture ne montre que la nouvelle activité, sans ajustements (live du 04/11) ;
 *  - **les quatre entrées arrivent au même brouillon**, sa pièce en tête (`composer.source`) : le configurateur, une
 *    image glissée n'importe où dans la salle ou collée hors d'un champ (`useTutorLiveRoomDrop`), une photo du chat
 *    avec le chapitre de l'activité en cours, « similaire » avec ses commandes (similaire, plus facile, plus dur) ;
 *    **la dernière arrivée l'emporte** : une similaire retire l'image (et sa lecture en vol), une image retire la
 *    similaire ;
 *  - la palette « / » du champ.
 *
 * **Le live du 04/11, câblé** :
 *  - **« + Activité »** de la barre (`summon`) : le champ ressort (`composer.attention`, le pop) et se **pré-remplit**
 *    pour l'élève visé (`prefill`, `generationPrefill`) : l'activité ouverte au centre, sinon la dernière activité
 *    rendue dans la séance, sinon celle qu'il a devant lui, posée en « similaire » ; sinon le chapitre de son
 *    historique ou la matière de l'objectif. Le focus du champ pré-remplit aussi, une fois par élève ; une pièce
 *    pré-remplie cède au « + Activité » suivant si la source a changé ; le tuteur retire la pièce d'un geste ;
 *  - **la version courte** : la pièce, les commandes à toucher, trois réglages sans pièce (chapitre, type, durée,
 *    plus celui qui manque) ; **« Plus d'options »** ouvre le configurateur au centre (`configurator`) : tout le
 *    formulaire, le même brouillon.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { type TextInput } from "react-native"
import { useDispatch, useStore } from "react-redux"

import { appendTutorLiveSession } from "../../../../../../data/redux/reducers/data/tutorLiveSession"
import { isPdfFile } from "../../../../../../data/utility/pdfHelpers"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type TutorSessionComposer } from "../../../../../../design-system/organisms/TutorSessionFeed"
import { type TutorSessionEntryProps } from "../../../../../../design-system/organisms/TutorSessionEntry"
import { type TutorSessionSettingSheetProps } from "../../../../../../design-system/templates/TutorSessionSettingSheet/TutorSessionSettingSheet"
import { endSentence } from "../../../formatters/textFormatter"
import { activityDraftOptions, activityDraftSettings } from "../../../formatters/tutorSessionFormatter"
import { formatTime } from "../../../formatters/dateFormatter"
import { useGenerateActivityFlow } from "../../../hooks/useGenerateActivityFlow"
import { useSourceChapterFix } from "../../../hooks/useSourceChapterFix"
import { useTutorImageCrop, type TutorImageCrop } from "../../../hooks/useTutorImageCrop"
import {
    type UIActivityFile,
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
    type UIActivityGenerationMissingField,
} from "../../../types"
import {
    type UITutorSessionEntry,
    type UITutorSessionSetting,
    type UITutorSessionSettingField,
} from "../../../types/tutorLive"
import {
    type TutorReviewPeekProps,
    type TutorReviewStageProps,
} from "../../../../../../design-system/organisms/TutorReviewStage"
import { type TutorGenerationConfiguratorProps } from "../../../../../../design-system/organisms/TutorGenerationConfigurator"
import {
    type TutorGenerationPrefill,
    type TutorGenerationPrefillKind,
} from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    describeInInstructions,
    missingStudentGenerationField,
} from "../../../../adapters/student/Activities/studentGenerationAdapter"
import {
    filterCommands,
    generationPhase,
    generationSteps,
    instructionsRefusal,
    modifierInstruction,
    paletteCommands,
    readyTitle,
    reviewOpensAlone,
    SIMILAR_MODIFIERS,
    unseenWhile,
    withAdjustments,
    type TutorGenerationSource,
    type TutorPaletteCommandKey,
    type TutorSimilarModifier,
} from "../../../../view-models/tutor/Live/tutorLiveGenerationRules"
import { useTutorLiveRoomDrop, webFile } from "./useTutorLiveRoomDrop"
import { droppedFile, pastedImage } from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import {
    SOURCE_CHAPTER_FIX_TEXTS,
    sourceChapterFixNotice,
    sourceNeedsChapter,
} from "../../../../view-models/tutor/Students/sourceChapterFix"
import { type TutorLiveTransientEntry } from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId } from "./tutorLiveContract"

const TEXTS = {
    placeholder: (target: string) => endSentence(`Une activité pour ${target}`, "…"),
    placeholderClass: "Une activité pour tout le groupe…",
    working: (helper: string) => `${helper} compose l'activité…`,
    failed: "La génération n'a pas abouti. Tes réglages sont gardés.",
    attached: "Support joint",
    similar: "Une autre comme celle-ci",
    created: "Activité ajoutée à sa liste",
    createdClass: "Activité ajoutée à la liste de chaque élève",
    shown: "Activité montrée à l'élève",
    group: "tout le groupe",
    preparing: (helper: string, count: number, target: string) =>
        `${helper} prépare ${count} propositions pour ${target}`,
    writing: (helper: string) => `${helper} écrit cette proposition…`,
    heading: (count: number, scope: string | null) =>
        `${count} proposition${count > 1 ? "s" : ""}${scope != null ? ` · ${scope}` : ""}`,
    readyFor: (target: string, settings: string) =>
        [`Pour ${target}`, settings].filter((part) => part !== "").join(" · "),
    sendFirst: "Ajouter et montrer la 1ʳᵉ",
    sendTo: "Ajouter et montrer",
    /** Hors live, rien ne se montre à l'élève : le geste principal ajoute à sa liste. */
    addTo: "Ajouter à la liste",
    dropped: "Image déposée",
    pastedImage: "Image collée",
    chatPhoto: (at: string | null) => (at != null ? `Photo du chat · ${at}` : "Photo du chat"),
    chapterKept: "Le chapitre de l'activité en cours est repris",
    like: (title: string) => `Comme « ${title} »`,
    likeHint: "Mêmes réglages que l'original",
    /** D'où vient la similaire pré-remplie : ce que dit sa pièce. */
    prefilled: {
        open: "L'activité ouverte",
        submitted: "Sa dernière activité rendue",
        current: "L'activité qu'il a devant lui",
    } satisfies Record<TutorGenerationPrefillKind, string>,
    createLocked: "Une activité part déjà vers l'élève : réessaie dans un instant.",
    createFailed: "L'activité n'a pas pu être ajoutée. Réessaie.",
    instructionsRefused: {
        short: "Précise un peu ta consigne : cinq caractères au moins.",
        long: "Ta consigne est trop longue : 500 caractères au plus.",
    },
    unreadable: (pdf: boolean) =>
        pdf
            ? "Le document n'a pas pu être lu : la demande partira sans lui. Retire-le, ou joins-en un autre."
            : "L'image n'a pas pu être lue : la demande partira sans elle. Retire-la, ou joins-en une autre.",
    dropRefused: "Ce fichier n'est pas accepté : dépose une image ou un PDF.",
    readStuck: "La lecture de l'image n'aboutit pas : rien n'est parti. Réessaie, ou retire l'image.",
    edit: {
        class: "La classe et la matière",
        subject: "La classe et la matière",
        chapter: "Le chapitre",
        level: "Le type",
        duration: "La durée",
        skills: "Les compétences",
    } satisfies Record<TutorLiveEditableSetting, string>,
}

/** Les réglages qui se déplient sur place ; « Personnaliser » bascule d'un toucher. */
export type TutorLiveEditableSetting = Exclude<UITutorSessionSettingField, "personalize">

/**
 * Les champs du formulaire qu'une puce déplie. La classe et la matière vont ensemble (`scope`) : une classe
 * inconnue (une activité commune) se choisit là, sinon elle bloquait sur « Sélectionner une classe et matière
 * d'abord ».
 */
export const TUTOR_LIVE_SETTING_FIELDS: Record<TutorLiveEditableSetting, UIActivityGenerationField[]> = {
    class: ["scope"],
    subject: ["scope"],
    // Les compétences du chapitre suivent son choix, sous la liste, comme l'ancien formulaire (weekly du 03/11).
    chapter: ["chapter", "skillParts"],
    level: ["level"],
    duration: ["duration"],
    skills: ["skillParts"],
}

/** La puce d'un champ qui manque (`missingField`) : celle que l'envoi déplie ; des instructions, le champ lui-même. */
export function settingOfMissingField(
    field: UIActivityGenerationMissingField | null | undefined
): TutorLiveEditableSetting | null {
    return field === "class" || field === "subject" || field === "chapter" ? field : null
}

/** Ce que le crochet du fil reçoit pour générer. */
export type TutorLiveFeedGenerationDeps = {
    /** La cible du champ : l'élève (le filtre), ou `null` pour tout le groupe. */
    targetId: string | null
    /** Les élèves inscrits (en chaînes), le premier tient lieu de représentant pour tout le groupe. */
    studentIds: string[]
    firstnameOf: (studentId: string) => string
    liveSessionId: number | null
    /** La salle offre le quiz de prérequis (`prerequisite-quiz`). */
    quizAvailable: boolean
    send:
        ((type: string, data: Record<string, unknown>, to: number[] | null, policy: "keep" | "drop") => unknown) | null
    memoryKey: string | null
    helperName: string
    notify: (message: string, tone: "success" | "danger" | "info") => void
    /**
     * La vue que le tuteur regarde (`TutorGenerationView`) : l'élève regardé, ou `null` pour le groupe. La relecture
     * ne s'ouvre seule que s'il la regarde encore quand les propositions arrivent.
     */
    watching: string | null
    /** Une présentation tourne ou part : la relecture ne s'ouvre que d'un geste (elle se poserait sur l'écran partagé). */
    presenting: boolean
    /** L'activité ouverte au centre pour un élève, de quoi en faire une semblable (« /similaire ») ; `null` sinon. */
    similarSource: (studentId: string) => NonNullable<TutorLiveFeedGenerationRequest["from"]> | null
    /** Le glisser et le collage dans toute la salle (au web) : faux quand le chat est ouvert, ou au centre seul. */
    roomDrop: boolean
    /**
     * Une autre création lit la même réponse (`createActivityAsTutor`) : « <champion> propose » envoie la sienne. Lu au
     * geste ; vrai : la création attend, et le tuteur le lit.
     */
    isCreateLocked?: () => boolean
    /**
     * Hors live (« Mes élèves » dans le cadre du live) : rien ne se montre à l'élève. « Ajouter et montrer » devient
     * « Ajouter à la liste », seul (`<TutorReviewStage offLive>`), et l'accusé dit l'ajout. Défaut `false`.
     */
    offLive?: boolean
    /**
     * Ce que le champ reprend pour un élève quand il s'ouvre (`generationPrefill`, lu au geste) ; absent ou `null` :
     * rien, le champ part de la classe et de la matière de l'élève.
     */
    prefill?: (studentId: string) => TutorGenerationPrefill | null
}

/**
 * Ce qu'un appelant demande : la cible (`null` : tout le groupe, ou `ids` quand il en nomme plusieurs), le
 * support d'une activité existante (ses similaires), ou une pièce à lire.
 */
export type TutorLiveFeedGenerationRequest = {
    target: string | null
    ids?: string[]
    /**
     * `confidence` : celle du chapitre de l'exo (`analysis.confidence`) ; sous `high`, son chapitre se choisit d'abord
     * (C-26), dans la matière du support (`subjectId`) et la classe de l'élève (à défaut, `className`, celle du support).
     */
    from?: {
        materialId: string | number
        title: string | null
        level: number | null
        confidence?: string | null
        subjectId?: string | number | null
        className?: string | null
        /** Ce que la relecture montre de l'original, à côté de la nouvelle (« similaire » côte à côte). */
        preview?: { statement?: string; imageUri?: string }
    }
    attachment?: UIActivityFile
    /** D'où vient la pièce : une photo du chat (et l'heure de son message), un dépôt, un collage. */
    origin?: { kind: "chat"; at?: number } | { kind: "drop" | "paste" | "pick" }
    /** Le chapitre de l'activité en cours de l'élève, que la photo du chat reprend. */
    chapter?: { chapterId: string; subjectId?: string | null }
    /** La similaire vient du pré-remplissage : sa pièce dit d'où (l'activité ouverte, la dernière rendue, celle en cours). */
    prefilled?: TutorGenerationPrefillKind
}

export type TutorLiveFeedGeneration = {
    composer: TutorSessionComposer
    /** Les lignes de la génération en cours : l'attente, les propositions (jamais persistées). */
    transient: TutorLiveTransientEntry[]
    onResult: NonNullable<TutorSessionEntryProps["onResult"]>
    /** Le nombre de générations parties : chacune agrandit la session (`useTutorLiveFeed`). */
    batch: number
    /**
     * La puce dépliée sur place, au-dessus du champ : les props de `<TutorSessionSettingSheet inline>`, que l'écran
     * pose dans le champ (`composer.editor`). Aucune : `null`.
     */
    settingEditor: TutorSessionSettingSheetProps | null
    /** « Recadrer l'image » d'une photo jointe au web (`useTutorImageCrop`), tant qu'elle attend ; sinon `null`. */
    crop: TutorImageCrop<{ pasted: boolean }>["step"]
    /** Joindre un support venu d'ailleurs (une photo du champion, G-18) ; `target` : l'élève visé, sinon le filtre. */
    attach: (file: UIActivityFile, target?: string | null) => void
    /**
     * Les trois appelants de l'écran : « Ajouter une activité » de la pellicule, « Générer à partir de cet exo »
     * du centre (le support posé dans le champ, ses réglages à modifier), « Ajouter une activité » de la barre et la
     * pièce du chat.
     */
    request: (request: TutorLiveFeedGenerationRequest) => void
    /** La relecture au centre (`<TutorReviewStage>`), ouverte ou non ; `null` sans propositions. */
    review: TutorReviewStageProps | null
    /** L'aperçu d'une proposition survolée dans la carte « prêtes ». */
    peek: TutorReviewPeekProps | null
    onPeek: (proposalId: string | null) => void
    /** « Revoir » et « Envoyer la 1ʳᵉ » de la carte « prêtes ». */
    onReady: (action: "review" | "sendFirst") => void
    /** Une image tenue au-dessus de la salle (web) : la page en fait la zone de dépôt. */
    dropping: boolean
    /** Une génération du champ est en vol (ses propositions s'écrivent, ou l'une se crée). */
    busy: boolean
    /** Une proposition du champ se crée (la tranche `createActivityAsTutor` attend sa réponse). */
    creating: boolean
    /**
     * Une création du champ attend sa réponse, au moment de la question : d'un lot d'avant compris, que « Autres
     * énoncés » a retiré de l'écran. La seule lecture de « une création à la fois », pour le champ comme pour le champion.
     */
    isCreating: () => boolean
    /**
     * « + Activité » : le champ ressort pour la cible (l'élève nommé, sinon celle du filtre), pré-rempli ; `null` :
     * tout le groupe.
     */
    summon: (target?: string | null) => void
    /** « Plus d'options » : le configurateur au centre, ouvert ou non ; `null` tant que le brouillon n'est pas là. */
    configurator: TutorGenerationConfiguratorProps | null
}

type StoredActivity = { id?: string | number; material?: { id?: string | number; title?: string | null } | null }
type CreatedResponse = {
    student_id?: string | number
    new_activities?: (StoredActivity & { student_id?: string | number })[]
}

/**
 * Le collage et le dépôt d'un support, écoutés sur le champ du fil seulement (tech-derisk, point 15, A ; le
 * legacy écoutait `paste` sur tout le document, `ImageUploadZone.js:118-144`) : un collage dans le chat, ou
 * n'importe où ailleurs, ne joint rien ici. Les images seules au collage ; une image ou un PDF au dépôt.
 * Rend de quoi se détacher.
 */
export function listenOnField(
    element: HTMLElement,
    receive: (file: UIActivityFile, fromPaste: boolean) => void,
    refuse?: () => void
): () => void {
    const onPaste = (event: ClipboardEvent) => {
        const image = pastedImage(event.clipboardData as unknown as Parameters<typeof pastedImage>[0])
        if (image == null) return
        event.preventDefault()
        receive(webFile(image), true)
    }
    const onDragOver = (event: DragEvent) => event.preventDefault()
    const onDrop = (event: DragEvent) => {
        const files = event.dataTransfer?.files
        const file = droppedFile(files)
        if (file == null && !files?.length) return
        event.preventDefault()
        // Un fichier d'un autre type : le dire, plutôt que l'ignorer (et ne pas laisser le navigateur l'ouvrir).
        if (file == null) refuse?.()
        else receive(webFile(file), false)
    }
    element.addEventListener("paste", onPaste as EventListener)
    element.addEventListener("dragover", onDragOver as EventListener)
    element.addEventListener("drop", onDrop as EventListener)
    return () => {
        element.removeEventListener("paste", onPaste as EventListener)
        element.removeEventListener("dragover", onDragOver as EventListener)
        element.removeEventListener("drop", onDrop as EventListener)
    }
}

export function useTutorLiveFeedGeneration(deps: TutorLiveFeedGenerationDeps): TutorLiveFeedGeneration {
    const dispatch = useDispatch()
    const store = useStore()
    const depsRef = useRef(deps)
    depsRef.current = deps
    const {
        targetId,
        studentIds,
        liveSessionId,
        quizAvailable,
        helperName,
        watching,
        presenting,
        offLive = false,
    } = deps

    // La cible ouverte : celle du brouillon. Le brouillon ne se rouvre qu'au premier geste pour une autre cible,
    // jamais au seul changement d'élève regardé : les propositions d'avant restent touchables.
    const [opened, setOpened] = useState<{ targetId: string | null; ids?: string[] } | null>(null)
    const openedTarget = opened?.targetId ?? null
    const openedIds = opened?.ids
    const classIds = useMemo(
        () => (openedTarget == null ? (openedIds ?? studentIds) : undefined),
        [openedTarget, openedIds, studentIds]
    )
    // Les élèves d'une activité commune partent en nombres, comme `selectedStudentIds` (les `id` de
    // `liveSession.users`) : `student_ids` et `live_session_id` à l'identique.
    const targetStudentIds = useMemo(() => classIds?.map(rawId), [classIds])
    const representative = openedTarget ?? classIds?.[0] ?? null

    const showAfterCreateRef = useRef<string | null>(null)
    // « Envoyer à Léa » : la proposition créée et montrée (l'accusé de la relecture).
    const [sentProposal, setSentProposal] = useState<string | null>(null)
    const onCreatedActivity = useCallback(
        (response: unknown) => {
            const d = depsRef.current
            const created = (response ?? {}) as CreatedResponse & StoredActivity
            const list = created.new_activities ?? [
                { ...created, student_id: created.student_id ?? openedTarget ?? undefined },
            ]
            for (const activity of list) {
                const studentId = activity.student_id ?? openedTarget
                if (studentId == null) continue
                // `newTodo` à l'élève, comme `GenerateActivityModal` en live : une par élève d'une activité commune.
                // `drop` : hors connexion, l'ancien `useLiveKitEventSender` ne gardait rien.
                d.send?.("newTodo", { id: rawId(String(studentId)), activity }, [Number(studentId)], "drop")
                if (showAfterCreateRef.current != null) {
                    d.send?.(
                        "newTodo",
                        { id: rawId(String(studentId)), activity, forceFocus: true },
                        [Number(studentId)],
                        "drop"
                    )
                }
            }
            d.notify(
                showAfterCreateRef.current != null
                    ? TEXTS.shown
                    : created.new_activities != null
                      ? TEXTS.createdClass
                      : TEXTS.created,
                "success"
            )
            showAfterCreateRef.current = null
        },
        [openedTarget]
    )
    // Une création refusée se dit, et son « montrer » ne passe pas à l'ajout suivant (qui l'ouvrait en grand chez
    // l'élève alors que le tuteur n'avait demandé qu'un ajout).
    const onCreateFailed = useCallback((error: unknown) => {
        showAfterCreateRef.current = null
        setSentProposal(null)
        depsRef.current.notify(apiErrorMessage(error, TEXTS.createFailed), "danger")
    }, [])

    // Une photo jointe au web (le trombone, le dépôt, le collage) passe d'abord par le recadrage, qui la joint
    // ensuite comme un support (`attach`, défini plus bas, qui ne la retient pas une seconde fois).
    const attachCroppedRef = useRef<(file: UIActivityFile, fromPaste: boolean) => void>(() => {})
    const imageCrop = useTutorImageCrop<{ pasted: boolean }>((file, meta) =>
        attachCroppedRef.current(file, meta?.pasted === true)
    )
    const flow = useGenerateActivityFlow({
        studentId: representative,
        targetStudentIds,
        liveSessionId,
        quizAvailable,
        onCreatedActivity,
        onCreateFailed,
        holdFile: imageCrop.hold,
    })
    const { generate } = flow

    // Le dernier rendu du moteur : un geste mis en attente le lit au moment où il part, jamais celui d'avant.
    const flowRef = useRef(flow)
    flowRef.current = flow

    /**
     * Le geste part sur le brouillon de la cible du moment : tout de suite s'il est ouvert pour elle, sinon
     * après l'ouverture (`open()` lit l'élève représentatif, et remet le brouillon à neuf : un geste parti avant
     * serait effacé). Vrai quand il attend l'ouverture.
     */
    const queueRef = useRef<(() => void)[]>([])
    const whenOpen = useCallback(
        (gesture: () => void, target: string | null = depsRef.current.targetId, ids?: string[]) => {
            const same =
                opened != null && opened.targetId === target && (opened.ids ?? []).join(",") === (ids ?? []).join(",")
            if (same && generate.visible) {
                gesture()
                return false
            }
            queueRef.current.push(gesture)
            setOpened({ targetId: target, ids })
            return true
        },
        [opened, generate.visible]
    )

    const openForRef = useRef<string | null | undefined>(undefined)
    const justOpenedRef = useRef(false)
    useEffect(() => {
        if (opened == null) return
        const key = `${opened.targetId}|${(opened.ids ?? []).join(",")}`
        if (openForRef.current !== key || (!flow.generate.visible && !justOpenedRef.current)) {
            openForRef.current = key
            justOpenedRef.current = true
            flow.open()
            // Ouvert pour une autre cible : le support « similaire » d'avant ne la suit pas (`open()` a remis
            // l'analyse à zéro, il partirait en bulle sans similaires).
            setSeed((current) => (current != null && current.target !== opened.targetId ? null : current))
            return
        }
        // Le rendu qui suit l'ouverture : le brouillon neuf est là, les gestes en attente partent.
        if (justOpenedRef.current && flow.generate.visible) {
            justOpenedRef.current = false
            const queued = queueRef.current
            queueRef.current = []
            queued.forEach((gesture) => gesture())
        }
    }, [opened, flow])

    // ── Le texte du champ, puis l'envoi une fois les instructions posées dans le brouillon.
    const [text, setText] = useState("")
    // L'envoi en vol : sa consigne, et la règle de ce qui peut manquer — celle du champ court (G-09), ou celle de
    // l'ancienne fenêtre pour le configurateur (`sendMissing` : un chapitre ou l'exercice décrit).
    // `typed` : ce que le tuteur a tapé, sans la phrase d'une pente — le fil le dit en message.
    const [pendingSend, setPendingSend] = useState<{
        instructions: string
        typed: string
        rule: "field" | "configurator"
    } | null>(null)
    const [batch, setBatch] = useState(0)
    // La consigne partie avec la dernière demande : elle reste au brouillon (« Autres énoncés » la relit), mais le
    // configurateur rouvert ne la reprend pas.
    const sentInstructionsRef = useRef<string | null>(null)
    const textRef = useRef(text)
    textRef.current = text
    // « Plus d'options » ouvert, pour sa cible ; `tried` : « Générer » a été touché, ce qui manque se dit.
    const [configurator, setConfigurator] = useState<{ target: string | null; tried: boolean } | null>(null)
    // Une recherche de chapitre sans issue a basculé vers les instructions : le formulaire le recommande.
    const [instructionsRecommended, setInstructionsRecommended] = useState(false)
    // Ce qui est parti, pour la ligne vivante, la carte « prêtes » et la relecture : la vue regardée à l'envoi, d'où
    // part la génération et ses réglages.
    const [sent, setSent] = useState<{
        view: string | null
        source: TutorGenerationSource
        thumbUri?: string
        settingsLabel: string
        scope: string | null
        at: number
    } | null>(null)
    // « Générer une activité similaire » : l'activité posée en support dans le champ (ce que sa puce dit), qui part
    // à l'envoi du masteur, ses réglages modifiés ou non.
    // Rattaché à sa cible : un autre élève regardé, ou la mosaïque, ne le reprend pas (il partait en demande
    // fantôme, relecture du 03/11).
    const [seed, setSeed] = useState<{
        target: string | null
        label: string
        source: NonNullable<TutorLiveFeedGenerationRequest["from"]>
        prefilled?: TutorGenerationPrefillKind
    } | null>(null)
    const seedRef = useRef(seed)
    seedRef.current = seed
    const openedRef = useRef(opened)
    openedRef.current = opened
    // Les élèves dont le champ a déjà sa pièce, pré-remplie ou choisie (une photo du chat, une image, « Générer à
    // partir de cet exo ») : le focus ne pré-remplit plus pour eux, et ne remplace jamais ce que le tuteur a posé.
    const prefilledRef = useRef(new Set<string>())
    const activeSeed = seed != null && seed.target === targetId ? seed : null
    const similarSeed = activeSeed?.label ?? null
    const seedSource = activeSeed?.source ?? null
    // La source retirée par « À partir de zéro » : « À partir d'une activité » la repose, même quand aucune activité
    // n'est ouverte au centre (la dernière rendue), et les deux onglets restent jusqu'à sa prochaine ouverture.
    const [setAside, setSetAside] = useState<{ target: string | null; source: typeof seedSource } | null>(null)
    const setAsideSource = setAside != null && setAside.target === targetId ? setAside.source : null
    // La puce dépliée sur place : une puce touchée, ou celle qui manque à un envoi.
    // Rattachée à sa cible, comme le support : sous « pour B », la puce de A ne reste pas dépliée.
    const [editingFor, setEditingFor] = useState<{ target: string | null; field: TutorLiveEditableSetting } | null>(
        null
    )
    const editing = editingFor != null && editingFor.target === targetId ? editingFor.field : null
    const setEditing = useCallback(
        (field: TutorLiveEditableSetting | null) =>
            setEditingFor(field == null ? null : { target: depsRef.current.targetId, field }),
        []
    )
    // ── Le chapitre peu sûr de l'activité posée en support : il se choisit dans la puce du chapitre, et l'envoi
    // attend qu'il soit enregistré (ou confirmé sans chapitre quand sa matière n'en a aucun dans la classe).
    const fix = useSourceChapterFix({
        source:
            seedSource != null
                ? {
                      materialId: seedSource.materialId,
                      confidence: seedSource.confidence ?? null,
                      subjectId: seedSource.subjectId ?? null,
                  }
                : null,
        studentId: representative,
        className: generate.draft.className ?? seedSource?.className ?? null,
        active: seedSource != null,
    })
    const fixRef = useRef(fix)
    fixRef.current = fix
    // Le chapitre choisi dans la puce d'un support peu sûr part aussitôt par la correction du chapitre (`pick` ne
    // renvoie pas celui déjà enregistré).
    const changeDraft = generate.onDraftChange
    const onDraftChange = useCallback(
        (draft: UIActivityGenerationDraft) => {
            if (draft.chapterId != null) setInstructionsRecommended(false)
            if (fixRef.current.shown && draft.chapterId != null) fixRef.current.pick(draft.chapterId)
            changeDraft(draft)
        },
        [changeDraft]
    )
    // Rien à choisir, ou la liste n'a pas pu se charger : le champ le dit une fois (le choix, lui, se lit dans la
    // puce dépliée). Un échec d'enregistrement aussi.
    const fixNotice =
        fix.status === "empty" || fix.status === "failed"
            ? sourceChapterFixNotice(fix.status, generate.scopeLabel ?? null)
            : null
    useEffect(() => {
        if (fixNotice != null) depsRef.current.notify(fixNotice, "info")
    }, [fixNotice])
    // Un enregistrement refusé se dit, et la puce reprend le chapitre enregistré.
    useEffect(() => {
        if (fix.error == null) return
        depsRef.current.notify(fix.error, "danger")
        const current = flowRef.current.generate
        changeDraft({ ...current.draft, chapterId: fixRef.current.chapterId ?? null })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fix.error])

    // Les propositions en cours viennent-elles du formulaire ou d'un support (« Régénérer » relance la même voie) ?
    const [mode, setMode] = useState<"form" | "similar">("form")

    // La configuration d'une demande, que sa bulle dit d'abord (round 4) : les réglages posés, sans ce qui manque.
    // Une similaire part de l'activité qu'elle reprend : le chapitre du brouillon (un réglage d'avant) n'en dit rien.
    const sentSettings = useMemo<UITutorSessionSetting[]>(
        () =>
            activityDraftSettings(generate.draft, generate.catalogs).filter(
                (setting) => !setting.missing && !(similarSeed != null && setting.field === "chapter")
            ),
        [generate.draft, generate.catalogs, similarSeed]
    )

    // Les raccourcis de « similaire » (même niveau, plus facile, plus dur) : une consigne de plus, à l'envoi.
    const [modifier, setModifier] = useState<TutorSimilarModifier>("same")
    const supported = similarSeed != null || generate.file != null
    // La palette « / » : les commandes que le texte tapé appelle ; « Entrée » prend la première.
    const similarFor = targetId != null ? deps.similarSource(targetId) : null
    const palette = filterCommands(text, paletteCommands(similarFor?.title ?? null))
    const onCommandRef = useRef<(key: string) => void>(() => {})

    const onSend = useCallback(() => {
        if (palette != null && palette.length > 0) {
            onCommandRef.current(palette[0].key)
            return
        }
        const instructions = withAdjustments(text, supported ? [modifierInstruction(modifier)] : [])
        whenOpen(() => setPendingSend({ instructions, typed: text, rule: "field" }))
    }, [whenOpen, text, palette, supported, modifier])

    // Une image encore en lecture : l'envoi l'attend, puis part sur elle (envoyé avant, il partait en demande de
    // formulaire et l'image restait de côté). Une lecture en échec part sans elle.
    const readingImage = similarSeed == null && generate.file != null && generate.analysis?.status === "analyzing"

    useEffect(() => {
        if (pendingSend == null || !generate.visible || justOpenedRef.current) return
        if (generate.draft.instructions !== pendingSend.instructions) {
            generate.onDraftChange({ ...generate.draft, instructions: pendingSend.instructions })
            return
        }
        if (readingImage) return
        const sending = pendingSend
        setPendingSend(null)
        // Un support : un fichier lu, ou l'activité posée par « similaire ». Le support d'un « similaire » déjà
        // envoyé ou retiré ne compte plus : la demande suivante est une demande, pas une similaire.
        const withSupport = similarSeed != null || (generate.file != null && generate.analysis?.status === "done")
        // Un champ manque : rien ne part, et l'envoi déplie sa puce (G-09, « le bouton amène au champ ») ; le texte
        // reste dans le champ. Les similaires ne demandent que le support.
        const missing =
            sending.rule === "configurator"
                ? sendMissing(generate.draft, generate.missingField)
                : (generate.missingField ?? null)
        if (!withSupport && missing != null) {
            setEditing(settingOfMissingField(missing))
            return
        }
        const refusal = instructionsRefusal(sending.instructions, withSupport)
        if (refusal != null) {
            depsRef.current.notify(TEXTS.instructionsRefused[refusal], "info")
            return
        }
        // Le support d'un « similaire » au chapitre peu sûr : son chapitre se choisit d'abord, dans sa puce ; sans
        // rien à choisir, l'envoi le confirme sans chapitre puis part.
        const unsure = similarSeed != null && fix.unreliable
        if (unsure && fix.status !== "empty") {
            if (fix.status === "failed") fix.retry()
            setEditing("chapter")
            return
        }
        const level = generate.draft.level
        // La demande s'écrit dans le fil et le champ se vide quand elle part vraiment : après la confirmation sans
        // chapitre, s'il en faut une — refusée, le support et le texte restent pour réessayer.
        const commit = () => {
            setEditing(null)
            sentInstructionsRef.current = sending.instructions
            const d = depsRef.current
            const file = flowRef.current.generate.file
            const image = file != null && !isPdfFile(file)
            setSent({
                view: d.watching,
                source: activeSeed != null ? "similar" : file != null ? (image ? "photo" : "document") : "text",
                thumbUri: activeSeed?.source.preview?.imageUri ?? (image ? file?.uri : undefined),
                settingsLabel: sentSettings
                    .filter((setting) => setting.field === "chapter" || setting.field === "duration")
                    .map((setting) => setting.label)
                    .join(" · "),
                scope:
                    sentSettings.find((setting) => setting.field === "chapter")?.label ??
                    sentSettings.find((setting) => setting.field === "subject")?.label ??
                    null,
                at: Date.now(),
            })
            setModifier("same")
            setConfigurator(null)
            dispatch(
                appendTutorLiveSession(d.memoryKey, {
                    id: `tutor-${Date.now()}`,
                    kind: "tutor",
                    studentId: d.targetId ?? undefined,
                    at: Date.now(),
                    origin: "local",
                    text:
                        sending.instructions !== ""
                            ? sending.instructions
                            : (similarSeed ?? (withSupport ? TEXTS.similar : "Une activité")),
                    settings: sentSettings,
                    request: {
                        // La pente vaut pour tout support : la similaire, une photo, un document.
                        modifier: withSupport
                            ? SIMILAR_MODIFIERS.find((entry) => entry.key === modifier)?.label
                            : undefined,
                        source: similarSeed ?? undefined,
                        message: sending.typed.trim() !== "" ? sending.typed.trim() : undefined,
                    },
                }) as never
            )
            setText("")
            setSeed(null)
            setBatch((value) => value + 1)
            setMode(withSupport ? "similar" : "form")
        }
        const launchSimilar = () => flowRef.current.generate.onGenerateSimilar?.(level)
        if (unsure) {
            fix.confirmWithoutChapter(() => {
                commit()
                launchSimilar()
            })
            return
        }
        commit()
        if (withSupport) launchSimilar()
        else generate.onGenerate?.()
    }, [
        pendingSend,
        readingImage,
        generate,
        similarSeed,
        activeSeed,
        modifier,
        dispatch,
        sentSettings,
        setEditing,
        fix,
    ])

    // Un envoi qui attend la lecture d'une image n'attend pas toujours : une lecture qui ne revient pas annule l'envoi, et
    // le dit (le tuteur réessaie, ou retire l'image).
    const holdingForRead = pendingSend != null && readingImage
    useEffect(() => {
        if (!holdingForRead) return undefined
        const timer = setTimeout(() => {
            setPendingSend(null)
            depsRef.current.notify(TEXTS.readStuck, "info")
        }, READ_HOLD_MS)
        return () => clearTimeout(timer)
    }, [holdingForRead])

    // Une pièce que le champion n'a pas pu lire (illisible, trop lourde) : la demande partira sans elle, et le champ le
    // dit une fois par pièce (il n'en disait rien, seul le configurateur le montrait).
    const unreadableFile =
        similarSeed == null && generate.file != null && generate.analysis?.status === "error" ? generate.file : null
    const unreadableUri = unreadableFile?.uri ?? null
    useEffect(() => {
        if (unreadableUri == null) return
        const pdf = isPdfFile(unreadableFile)
        depsRef.current.notify(TEXTS.unreadable(pdf), "info")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unreadableUri])

    // ── Les puces : chacune se déplie sur place (la même la replie) ; « Personnaliser » bascule d'un toucher.
    const onEditSetting = useCallback(
        (field: UITutorSessionSettingField) => {
            whenOpen(() => {
                if (field === "personalize") {
                    const current = flowRef.current.generate
                    current.onDraftChange({
                        ...current.draft,
                        personalizeWithInterests: !current.draft.personalizeWithInterests,
                    })
                    return
                }
                setEditingFor((open) =>
                    open?.field === field && open.target === depsRef.current.targetId
                        ? null
                        : { target: depsRef.current.targetId, field }
                )
            })
        },
        [whenOpen]
    )

    // ── Le support joint : le trombone, le dépôt, le collage ; une image se recadre au web.
    // D'où vient le support joint (un dépôt, un collage, le trombone, une photo du chat) : sa pièce le dit.
    const [origin, setOrigin] = useState<NonNullable<TutorLiveFeedGenerationRequest["origin"]> | null>(null)
    // Le chapitre de l'activité en cours, que la photo du chat reprend : posé une fois le brouillon ouvert.
    const [chapterKept, setChapterKept] = useState(false)
    const attach = useCallback(
        (
            file: UIActivityFile,
            target?: string | null,
            ids?: string[],
            from: NonNullable<TutorLiveFeedGenerationRequest["origin"]> = { kind: "pick" },
            chapter?: TutorLiveFeedGenerationRequest["chapter"]
        ) => {
            const owner = target === undefined ? depsRef.current.targetId : target
            if (owner != null) prefilledRef.current.add(owner)
            setSeed(null)
            setOrigin(from)
            setChapterKept(chapter != null)
            setModifier("same")
            whenOpen(
                () => {
                    // La dernière arrivée l'emporte : l'image prend la place d'une similaire posée avant elle.
                    flowRef.current.clearMaterial()
                    flowRef.current.attachFile(file)
                    if (chapter == null) return
                    // Avant la lecture de la photo : son chapitre proposé ne remplace pas un chapitre déjà choisi.
                    const current = flowRef.current.generate
                    current.onDraftChange({
                        ...current.draft,
                        subjectId: chapter.subjectId ?? current.draft.subjectId,
                        chapterId: chapter.chapterId,
                        skillPartPositions: [],
                    })
                },
                target,
                ids
            )
        },
        [whenOpen]
    )
    // Une image déposée passe par le recadrage avant d'être jointe : sa pièce dit encore qu'elle a été déposée.
    const dropViaRef = useRef(false)
    attachCroppedRef.current = (file, fromPaste) => {
        const via = fromPaste ? "paste" : dropViaRef.current ? "drop" : "pick"
        dropViaRef.current = false
        attach(file, undefined, undefined, { kind: via })
    }
    const { hold, clear: clearCrop } = imageCrop
    // Les adresses `blob:` d'un collage ou d'un dépôt (`webFile`) : la pièce et sa vignette les lisent tant que la salle
    // est ouverte, elles se libèrent quand elle se ferme.
    const blobUrisRef = useRef(new Set<string>())
    useEffect(
        () => () => {
            blobUrisRef.current.forEach((uri) => URL.revokeObjectURL(uri))
            blobUrisRef.current.clear()
        },
        []
    )
    const refuseDrop = useCallback(() => depsRef.current.notify(TEXTS.dropRefused, "info"), [])
    const receive = useCallback(
        (file: UIActivityFile, fromPaste = false) => {
            if (file.uri.startsWith("blob:")) blobUrisRef.current.add(file.uri)
            dropViaRef.current = !fromPaste
            if (!hold(file, { pasted: fromPaste })) {
                attach(file, undefined, undefined, { kind: fromPaste ? "paste" : "drop" })
                dropViaRef.current = false
            }
        },
        [attach, hold]
    )
    // Une image glissée n'importe où dans la salle, ou collée hors d'un champ (web).
    const { dropping } = useTutorLiveRoomDrop(
        useCallback((file: UIActivityFile, via: "drop" | "paste") => receive(file, via === "paste"), [receive]),
        deps.roomDrop,
        refuseDrop
    )
    const inputRef = useRef<TextInput | null>(null)
    // Le champ demandé avant d'exister (la colonne qui revient, le panneau du téléphone qui s'ouvre, la fiche qui se
    // referme : il ne naît qu'au rendu suivant) prend le focus au rendu qui le monte, dans la seconde du geste.
    const focusWantedAtRef = useRef<number | null>(null)
    const focusField = useCallback(() => {
        if (inputRef.current != null) {
            focusWantedAtRef.current = null
            inputRef.current.focus()
        } else focusWantedAtRef.current = Date.now()
    }, [])
    useEffect(() => {
        const wantedAt = focusWantedAtRef.current
        if (wantedAt == null || inputRef.current == null) return
        focusWantedAtRef.current = null
        if (Date.now() - wantedAt < FOCUS_WAIT_MS) inputRef.current.focus()
    })
    const receiveRef = useRef(receive)
    receiveRef.current = receive
    // Le champ ne naît qu'avec la colonne (un tiroir fermé, la mosaïque sans session) : à chaque rendu, l'écoute
    // suit l'élément du moment.
    const listenedRef = useRef<{ element: HTMLElement; detach: () => void } | null>(null)
    useEffect(() => {
        if (!IS_WEB) return
        const element = inputRef.current as unknown as HTMLElement | null
        if (listenedRef.current?.element === element) return
        listenedRef.current?.detach()
        listenedRef.current = null
        if (element == null || typeof element.addEventListener !== "function") return
        listenedRef.current = {
            element,
            detach: listenOnField(element, (file, fromPaste) => receiveRef.current(file, fromPaste), refuseDrop),
        }
    })
    useEffect(() => () => listenedRef.current?.detach(), [])

    // ── Les propositions, en lignes du fil.
    const generated = generate.results
    const results = useMemo(() => generated ?? [], [generated])
    const creatingCard = results.some((card) => card.creation === "creating")
    const targetName = openedTarget != null ? deps.firstnameOf(openedTarget) : TEXTS.group
    const phase = generationPhase(results)

    // ── La relecture au centre : la fournée qu'elle montre, ouverte ou non, la proposition regardée, et pendant qui
    // elle est arrivée quand elle ne s'est pas ouverte seule.
    const [review, setReview] = useState<{
        batch: number
        open: boolean
        index: number
        unseen: string | null
    } | null>(null)
    const reviewHere = review != null && review.batch === batch ? review : null
    const firstReady = Math.max(
        0,
        results.findIndex((card) => card.status === "ready")
    )
    useEffect(() => {
        if (phase !== "ready" || reviewHere != null) return
        const d = depsRef.current
        const alone = reviewOpensAlone({ sentFrom: sent?.view ?? null, watching: d.watching, presenting: d.presenting })
        const watchingName = d.watching != null ? d.firstnameOf(d.watching) : TEXTS.group
        setReview({ batch, open: alone, index: firstReady, unseen: unseenWhile(watchingName, alone) })
    }, [phase, reviewHere, batch, sent?.view, firstReady])
    // Un autre élève regardé : la relecture se referme (elle était pour la vue d'avant) ; « Revoir » la rouvre.
    useEffect(() => {
        setReview((current) => (current?.open === true ? { ...current, open: false } : current))
    }, [watching])
    const openReview = useCallback(
        () =>
            setReview((current) =>
                current != null && current.batch === batch
                    ? { ...current, open: true, unseen: null }
                    : { batch, open: true, index: firstReady, unseen: null }
            ),
        [batch, firstReady]
    )
    const closeReview = useCallback(
        () => setReview((current) => (current != null ? { ...current, open: false } : current)),
        []
    )
    const [peekId, setPeekId] = useState<string | null>(null)

    // La ligne vivante tant qu'aucune proposition n'est là, puis la carte « prêtes » ; toutes en échec, la ligne le
    // dit et propose « Réessayer ». Une seule entrée par fournée (rien n'est persisté).
    const transient = useMemo<TutorLiveTransientEntry[]>(() => {
        if (phase === "idle") return []
        const at = sent?.at ?? Date.now()
        const iso = new Date(at).toISOString()
        const studentId = openedTarget ?? undefined
        let entry: UITutorSessionEntry
        if (phase === "writing") {
            entry = {
                kind: "progress",
                id: `progress-${batch}`,
                at: iso,
                startedAt: iso,
                title: TEXTS.preparing(helperName, results.length, targetName),
                thumbUri: sent?.thumbUri,
                steps: generationSteps({
                    source: sent?.source ?? "text",
                    helperName,
                    settingsLabel: sent?.settingsLabel ?? "",
                    results,
                }),
            }
        } else if (phase === "failed") {
            entry = { kind: "result", id: `result-${batch}`, at: iso, activities: [], error: TEXTS.failed }
        } else {
            entry = {
                kind: "ready",
                id: `ready-${batch}`,
                at: iso,
                title: readyTitle(results),
                caption: TEXTS.readyFor(targetName, sent?.settingsLabel ?? ""),
                proposals: results.map((card) => ({
                    id: String(card.id),
                    label: card.variationLabel ?? "",
                    title: card.title ?? TEXTS.writing(helperName),
                    status: card.status,
                })),
                reviewing: reviewHere?.open === true || undefined,
                unseen: reviewHere?.unseen ?? undefined,
                sendLabel: offLive ? TEXTS.addTo : results.length > 1 ? TEXTS.sendFirst : TEXTS.sendTo,
                // Une proposition ajoutée décide la demande : elle quitte « À traiter » et devient un point du fil.
                decided: results.some((card) => card.creation === "created") || undefined,
            }
        }
        return [{ studentId, at, entry }]
    }, [phase, results, batch, helperName, openedTarget, sent, targetName, reviewHere, offLive])

    const regenerate = useCallback(() => {
        setBatch((value) => value + 1)
        // Une relecture ouverte suit la fournée qui s'écrit (dans le même rendu) : elle montre les propositions en cours,
        // depuis la première, au lieu de se refermer jusqu'à leur arrivée.
        setReview((current) =>
            current?.open === true ? { ...current, batch: current.batch + 1, index: 0, unseen: null } : current
        )
        // La ligne vivante d'une nouvelle fournée compte ses secondes depuis la relance.
        setSent((current) => (current != null ? { ...current, at: Date.now() } : current))
        if (mode === "form" && generate.onRegenerate != null) generate.onRegenerate()
        else generate.onGenerateSimilar?.(generate.draft.level)
    }, [generate, mode])

    // Hors live, « montrer » n'a pas de salle : le geste principal (relecture, « la 1ʳᵉ ») est un ajout.
    const sendAction = offLive ? "create" : "show"
    const isCreating = useCallback(() => flowRef.current.isCreating(), [])

    const onResult = useCallback<TutorLiveFeedGeneration["onResult"]>(
        (_entryId, action, activityId) => {
            if (action === "regenerate" || action === "retry") {
                regenerate()
                return
            }
            const card = results.find((result) => String(result.id) === String(activityId))
            if (card == null) return
            if (action === "quiz") {
                generate.onToggleResultQuiz?.(card.id, card.quizEnabled !== true)
                return
            }
            if (card.creation === "created" || card.creation === "creating") return
            // Une autre création attend sa réponse (une autre proposition, d'un lot d'avant compris, ou celle du
            // champion) : leurs réponses se croiseraient sur la même tranche, la première marquait la seconde.
            if (flowRef.current.isCreating() || depsRef.current.isCreateLocked?.() === true) {
                depsRef.current.notify(TEXTS.createLocked, "info")
                return
            }
            showAfterCreateRef.current = action === "show" ? String(card.id) : null
            if (action === "show") setSentProposal(String(card.id))
            generate.onCreateResult?.(card.id)
        },
        [results, generate, regenerate]
    )

    // L'accusé de « Envoyer à Léa » tient un instant, puis la relecture se referme et le centre montre ce que l'élève a
    // maintenant devant lui.
    const sentCreated = results.some((card) => String(card.id) === sentProposal && card.creation === "created")
    useEffect(() => {
        if (!sentCreated) return undefined
        const timer = setTimeout(() => {
            closeReview()
            setSentProposal(null)
        }, SENT_ACK_MS)
        return () => clearTimeout(timer)
    }, [sentCreated, closeReview])

    const request = useCallback(
        (req: TutorLiveFeedGenerationRequest) => {
            setEditing(null)
            if (req.attachment != null) {
                attach(req.attachment, req.target, req.ids, req.origin ?? { kind: "pick" }, req.chapter)
                return
            }
            const from = req.from
            if (from != null) {
                // Le support posé dans le champ, ses réglages préremplis : rien ne part avant l'envoi du masteur. Un
                // chapitre peu sûr : la puce du chapitre se déplie sur la matière du support, où il se choisit.
                const unsure = sourceNeedsChapter(
                    from.materialId,
                    from.confidence,
                    (store.getState() as { data?: { materials?: { chapterConfidenceById?: Record<string, string> } } })
                        ?.data?.materials?.chapterConfidenceById
                )
                if (req.target != null) prefilledRef.current.add(req.target)
                setModifier("same")
                setSeed({
                    target: req.target,
                    label: from.title != null ? `${TEXTS.similar} : « ${from.title} »` : TEXTS.similar,
                    source: from,
                    prefilled: req.prefilled,
                })
                // La dernière arrivée l'emporte : une image qui attend son recadrage, ou déjà jointe, se retire.
                clearCrop()
                setOrigin(null)
                setChapterKept(false)
                focusField()
                whenOpen(
                    () => {
                        const current = flowRef.current
                        if (current.generate.file != null) current.generate.onRemoveFile?.()
                        const draft = current.generate.draft
                        current.generate.onDraftChange({
                            ...draft,
                            level: from.level != null ? String(from.level) : draft.level,
                            ...(unsure && {
                                subjectId: from.subjectId != null ? String(from.subjectId) : draft.subjectId,
                                className: draft.className ?? from.className ?? null,
                                chapterId: null,
                                skillPartPositions: [],
                            }),
                        })
                        current.seedMaterial({ id: from.materialId, title: from.title })
                        if (unsure) setEditing("chapter")
                    },
                    req.target,
                    req.ids
                )
                return
            }
            whenOpen(() => undefined, req.target, req.ids)
            focusField()
        },
        [attach, whenOpen, setEditing, store, clearCrop, focusField]
    )

    // ── Le pré-remplissage (« + Activité », le premier focus pour un élève) : la similaire de l'activité ouverte au
    // centre, sinon de sa dernière activité rendue ou de celle qu'il a devant lui, sinon le chapitre de son historique,
    // sinon la matière de l'objectif.
    // Une pièce que le tuteur a posée (une image, « Générer à partir de cet exo ») ne se remplace jamais ; une pièce
    // pré-remplie, si : « + Activité » relit la source, l'activité ouverte entre-temps l'emporte.
    const hasChosenPieceFor = useCallback(
        (studentId: string | null) =>
            (seedRef.current != null && seedRef.current.target === studentId && seedRef.current.prefilled == null) ||
            (flowRef.current.generate.file != null && openedRef.current?.targetId === studentId),
        []
    )
    const applyPrefill = useCallback(
        (studentId: string) => {
            prefilledRef.current.add(studentId)
            if (hasChosenPieceFor(studentId)) {
                whenOpen(() => undefined, studentId)
                return
            }
            const found = depsRef.current.prefill?.(studentId) ?? null
            const kept = seedRef.current?.target === studentId ? seedRef.current : null
            // Rien de neuf, ou la même activité déjà posée : rien ne se remet à zéro (la pente choisie, le texte tapé
            // restent) ; seule sa légende suit d'où elle vient maintenant.
            const same =
                found != null && found.kind !== "history" && kept?.source.materialId === found.source.materialId
            if (found == null || same) {
                if (same && kept.prefilled !== found.kind) setSeed({ ...kept, prefilled: found.kind })
                whenOpen(() => undefined, studentId)
                return
            }
            if (found.kind === "history") {
                // Un chapitre remplace la pièce pré-remplie d'avant : elle ne dirait plus ce que le champ propose.
                if (kept != null) {
                    setSeed(null)
                    setModifier("same")
                    flowRef.current.clearMaterial()
                }
                whenOpen(() => {
                    const current = flowRef.current.generate
                    // Un chapitre déjà au brouillon (choisi par le tuteur, ou posé par un « + Activité » d'avant) reste.
                    if (current.draft.chapterId != null) return
                    current.onDraftChange({
                        ...current.draft,
                        subjectId: found.subjectId ?? current.draft.subjectId,
                        chapterId: found.chapterId,
                        skillPartPositions: [],
                    })
                }, studentId)
                return
            }
            request({ target: studentId, from: { ...found.source }, prefilled: found.kind })
        },
        [whenOpen, request, hasChosenPieceFor, setModifier]
    )
    // Le pop : chaque « + Activité » fait monter le champ d'un cran.
    const [attention, setAttention] = useState(0)
    const summon = useCallback(
        (to?: string | null) => {
            const target = to === undefined ? depsRef.current.targetId : to
            setAttention((value) => value + 1)
            setEditing(null)
            // Une pièce posée par le tuteur, ou des propositions en cours : rien ne se remplace, le champ ressort. La
            // décision se prend avant le focus, que le champ lit aussi.
            const busyHere = flowRef.current.generate.results?.some((card) => card.status === "pending") === true
            if (busyHere || hasChosenPieceFor(target)) {
                if (target != null) prefilledRef.current.add(target)
                whenOpen(() => undefined, target)
            } else if (target != null) applyPrefill(target)
            else whenOpen(() => undefined, null)
            focusField()
        },
        [applyPrefill, whenOpen, setEditing, hasChosenPieceFor, focusField]
    )

    const onDescribeInInstructions = useCallback((searchText: string) => {
        setInstructionsRecommended(true)
        const current = flowRef.current.generate
        const next = describeInInstructions(current.draft, searchText)
        if (next !== current.draft) current.onDraftChange(next)
    }, [])

    // ── « Plus d'options » : le configurateur au centre, rattaché à sa cible comme la puce dépliée.
    const configuratorHere = configurator != null && configurator.target === targetId ? configurator : null
    const clearedInstructionsRef = useRef<string | null>(null)
    const closeConfigurator = useCallback(() => {
        setConfigurator(null)
        const cleared = clearedInstructionsRef.current
        clearedInstructionsRef.current = null
        const current = flowRef.current.generate
        if (cleared != null && current.draft.instructions === "")
            current.onDraftChange({ ...current.draft, instructions: cleared })
    }, [])
    const openConfigurator = useCallback(() => {
        setEditing(null)
        whenOpen(() => {
            // Le texte déjà tapé devient les instructions du formulaire : rien ne se perd en changeant de vue. La consigne
            // déjà partie, elle, ne repart pas avec le « Générer » suivant.
            const typed = textRef.current.trim()
            const current = flowRef.current.generate
            const alreadySent = current.draft.instructions === sentInstructionsRef.current
            const kept = alreadySent ? "" : current.draft.instructions
            // Le brouillon est aussi celui que relit « Autres énoncés » : refermé sans « Générer », il la retrouve.
            clearedInstructionsRef.current = alreadySent && typed === "" ? current.draft.instructions : null
            const instructions = withAdjustments(kept, [typed])
            if (instructions !== current.draft.instructions) current.onDraftChange({ ...current.draft, instructions })
            if (typed !== "") setText("")
            setSetAside(null)
            setConfigurator({ target: depsRef.current.targetId, tried: false })
        })
    }, [whenOpen, setEditing])

    const target = targetId != null ? deps.firstnameOf(targetId) : null
    const openedHere = opened != null && opened.targetId === targetId
    const fileAttached = generate.file != null
    // La version courte : sans pièce, le chapitre, le type et la durée (et le réglage qui manque) ; avec une pièce,
    // ses commandes seules. Tout le reste est dans « Plus d'options ».
    const shortFields = (field: UITutorSessionSettingField, missing: boolean) =>
        !supported && (SHORT_SETTINGS.has(field) || missing || field === editing)
    const composer: TutorSessionComposer = {
        value: text,
        onChangeText: setText,
        onSend,
        placeholder: target != null ? TEXTS.placeholder(target) : TEXTS.placeholderClass,
        // Un envoi qui attend la lecture de l'image se dit en cours, comme la génération.
        sending: generate.generating || results.some((card) => card.status === "pending") || pendingSend != null,
        // La configuration suffit à partir, sans texte, quand rien ne manque.
        ready: openedHere && generate.missingField == null,
        attention,
        onMoreOptions: () => openConfigurator(),
        // Les réglages du brouillon ouvert pour la cible du moment : sous « pour B », jamais ceux de A.
        settings: openedHere
            ? [
                  ...activityDraftSettings(generate.draft, generate.catalogs).map((setting) =>
                      setting.missing || setting.field !== generate.missingField
                          ? setting
                          : { ...setting, missing: true }
                  ),
                  ...activityDraftOptions(generate.draft, { interests: generate.studentInterests }),
              ]
                  .map((setting) =>
                      editing != null &&
                      (setting.field === editing || (editing === "class" && setting.field === "subject"))
                          ? { ...setting, open: true }
                          : setting
                  )
                  .filter((setting) => shortFields(setting.field, setting.missing === true))
            : undefined,
        onEditSetting,
        // Le champ pris : la génération s'ouvre pour la cible, ses réglages paraissent avant tout envoi (ils ne
        // paraissaient qu'après le premier, marche du 03/11).
        // Le focus prépare la génération de la cible, sans rien effacer : des propositions encore là (d'un
        // autre élève, ou en vol) attendent le premier vrai geste (l'envoi, une puce, le trombone) — un focus
        // n'est pas une demande (relecture du 03/11).
        onFocus: () => {
            if (results.length > 0) return
            // Le premier focus pour un élève le pré-remplit ; ensuite, il ne fait que préparer le brouillon. La cible
            // se lit au moment du focus (« + Activité » depuis la pellicule change de cible dans le même geste).
            const focused = depsRef.current.targetId
            if (focused != null && !prefilledRef.current.has(focused)) applyPrefill(focused)
            else if (!openedHere) whenOpen(() => undefined)
        },
        attachSources: ["document"],
        onAttach: () => whenOpen(() => void flowRef.current.generate.onPickFile?.()),
        inputRef,
        source: fileAttached
            ? fileSource(generate.file, origin, chapterKept, () => {
                  setOrigin(null)
                  setChapterKept(false)
                  generate.onRemoveFile?.()
              })
            : activeSeed != null
              ? {
                    kind: "similar",
                    title: activeSeed.source.title != null ? TEXTS.like(activeSeed.source.title) : TEXTS.similar,
                    caption: activeSeed.prefilled != null ? TEXTS.prefilled[activeSeed.prefilled] : TEXTS.likeHint,
                    thumbUri: activeSeed.source.preview?.imageUri,
                    onRemove: () => {
                        setSeed(null)
                        setModifier("same")
                        flowRef.current.clearMaterial()
                    },
                }
              : undefined,
        modifiers: supported
            ? SIMILAR_MODIFIERS.map((entry) => ({
                  key: entry.key,
                  label: entry.label,
                  selected: entry.key === modifier,
              }))
            : undefined,
        onModifier: (key) => setModifier(key as TutorSimilarModifier),
        palette,
        onCommand: (key) => onCommandRef.current(key),
    }

    // ── La palette : « /similaire » et ses pentes partent de l'activité ouverte au centre ; les réglages se déplient.
    onCommandRef.current = (key: string) => {
        const command = key as TutorPaletteCommandKey
        setText("")
        if (command === "similar" || command === "easier" || command === "harder") {
            const d = depsRef.current
            const from = d.targetId != null ? d.similarSource(d.targetId) : null
            if (from == null) return
            request({ target: d.targetId, from })
            setModifier(command === "similar" ? "same" : command)
            return
        }
        whenOpen(() => setEditing(command))
    }

    // ── La relecture et l'aperçu, tels que la page les pose sur le centre.
    const regenerateRef = useRef(regenerate)
    regenerateRef.current = regenerate
    const ready = results.filter((card) => card.status === "ready")
    const reviewIndex = Math.min(reviewHere?.index ?? 0, Math.max(0, results.length - 1))
    const reviewCard = results[reviewIndex]
    const stage: TutorReviewStageProps | null =
        reviewHere != null && results.length > 0
            ? {
                  open: reviewHere.open,
                  studentName: targetName,
                  heading: TEXTS.heading(results.length, sent?.scope ?? null),
                  proposals: results,
                  index: reviewIndex,
                  onChangeIndex: (index) => setReview((current) => (current != null ? { ...current, index } : current)),
                  quizAvailable,
                  onToggleQuiz:
                      reviewCard != null ? () => onResult(`review-${batch}`, "quiz", reviewCard.id) : undefined,
                  onSend: () => reviewCard != null && onResult(`review-${batch}`, sendAction, reviewCard.id),
                  // Hors live, le geste principal EST l'ajout : pas de second bouton.
                  onAddOnly: offLive
                      ? undefined
                      : () => reviewCard != null && onResult(`review-${batch}`, "create", reviewCard.id),
                  offLive,
                  // Régénérer garde les ajustements choisis (la consigne du brouillon est celle des puces), et la relecture.
                  onRegenerate: () => regenerateRef.current(),
                  onClose: closeReview,
                  sentId: sentProposal,
                  presenting,
                  writingLabel: TEXTS.writing(helperName),
              }
            : null
    const peeked = peekId != null ? ready.find((card) => String(card.id) === peekId) : undefined
    const peek: TutorReviewPeekProps | null =
        peeked != null && reviewHere?.open !== true ? { proposal: peeked, studentName: targetName } : null
    const onReady = useCallback(
        (action: "review" | "sendFirst") => {
            if (action === "review") {
                setPeekId(null)
                openReview()
                return
            }
            const first = results.find((card) => card.status === "ready")
            if (first != null) onResult(`ready-${batch}`, sendAction, first.id)
        },
        [openReview, results, onResult, batch, sendAction]
    )

    // Les props de la puce dépliée, d'une identité stable tant que rien ne change (le `useMemo` de l'écran).
    const settingEditor = useMemo<TutorSessionSettingSheetProps | null>(
        () =>
            editing != null && generate.visible
                ? {
                      inline: true,
                      visible: true,
                      onClose: () => setEditing(null),
                      title:
                          editing === "chapter" && fix.unreliable ? SOURCE_CHAPTER_FIX_TEXTS.pick : TEXTS.edit[editing],
                      fields: TUTOR_LIVE_SETTING_FIELDS[editing],
                      draft: generate.draft,
                      onDraftChange,
                      catalogs: fix.shown
                          ? { ...generate.catalogs, chapters: fix.sectionedChapters }
                          : generate.catalogs,
                      chaptersLoading: fix.shown ? fix.status === "loading" : generate.chaptersLoading,
                      skillPartsLoading: generate.skillPartsLoading,
                      studentInterests: generate.studentInterests,
                      nextAssessment: generate.nextAssessment,
                  }
                : null,
        [
            editing,
            setEditing,
            fix.unreliable,
            fix.shown,
            fix.status,
            fix.sectionedChapters,
            onDraftChange,
            generate.visible,
            generate.draft,
            generate.catalogs,
            generate.chaptersLoading,
            generate.skillPartsLoading,
            generate.studentInterests,
            generate.nextAssessment,
        ]
    )

    // Le configurateur : le formulaire entier sur le même brouillon ; « Générer » part comme l'envoi du champ, la
    // consigne étant celle du formulaire. Un champ qui manque se dit dans le formulaire, rien ne part.
    const configuratorMissing = sendMissing(generate.draft, generate.missingField)
    const configuratorProps: TutorGenerationConfiguratorProps | null =
        configuratorHere != null && openedHere && generate.visible
            ? {
                  open: true,
                  studentName: targetName,
                  form: {
                      value: generate.draft,
                      onChange: onDraftChange,
                      catalogs: fix.shown
                          ? { ...generate.catalogs, chapters: fix.sectionedChapters }
                          : generate.catalogs,
                      chaptersLoading: fix.shown ? fix.status === "loading" : generate.chaptersLoading,
                      chaptersFailed: generate.chaptersFailed,
                      onRetryChapters: generate.onRetryChapters,
                      skillPartsLoading: generate.skillPartsLoading,
                      studentInterests: generate.studentInterests,
                      nextAssessment: generate.nextAssessment,
                      fields:
                          similarSeed != null
                              ? SIMILAR_FORM_FIELDS
                              : fileAttached
                                ? FILE_FORM_FIELDS
                                : FULL_FORM_FIELDS,
                      chapterRequired: generate.chapterRequired,
                      // Sans pièce : un chapitre, OU l'exercice décrit dans les instructions (l'ancienne fenêtre) ; une
                      // recherche de chapitre sans issue y bascule.
                      chapterOrInstructions: !supported,
                      onDescribeInInstructions: supported ? undefined : onDescribeInInstructions,
                      instructionsRecommended:
                          !supported && instructionsRecommended && generate.draft.chapterId == null,
                      fieldToFix: configuratorHere.tried && !supported ? configuratorMissing : null,
                  },
                  source: composer.source,
                  // Une image : ce que le champion en a lu (le chapitre qu'il a choisi, une autre classe à accepter).
                  analysis:
                      similarSeed == null && fileAttached && generate.analysis != null
                          ? {
                                analysis: generate.analysis,
                                scopeLabel: generate.scopeLabel ?? undefined,
                                hasChapter: generate.draft.chapterId != null,
                                onAcceptCrossClass: generate.onAcceptCrossClass,
                                onDismissCrossClass: generate.onDismissCrossClass,
                            }
                          : undefined,
                  onAttach: supported
                      ? undefined
                      : composer.onAttach != null
                        ? () => composer.onAttach?.("document")
                        : undefined,
                  generating: composer.sending === true,
                  onGenerate: () => {
                      if (!supported && configuratorMissing != null) {
                          setConfigurator((current) => (current != null ? { ...current, tried: true } : current))
                          return
                      }
                      const instructions = withAdjustments(generate.draft.instructions, [
                          text,
                          ...(supported ? [modifierInstruction(modifier)] : []),
                      ])
                      // Une consigne que le serveur refuserait : le formulaire reste ouvert, où elle se corrige.
                      const refusal = instructionsRefusal(instructions, supported)
                      if (refusal != null) {
                          deps.notify(TEXTS.instructionsRefused[refusal], "info")
                          return
                      }
                      clearedInstructionsRef.current = null
                      setConfigurator(null)
                      const typed = withAdjustments(generate.draft.instructions, [text])
                      whenOpen(() => setPendingSend({ instructions, typed, rule: "configurator" }))
                  },
                  onClose: closeConfigurator,
                  presenting,
                  // Les deux modes, dès qu'une activité peut servir de source (la similaire posée, l'activité
                  // ouverte, ou celle que « de zéro » vient de retirer) : « de zéro » retire la source, « d'une
                  // activité » la repose comme « Similaire ».
                  mode:
                      similarSeed != null || setAsideSource != null || (similarFor != null && !fileAttached)
                          ? {
                                value: similarSeed != null ? "activity" : "scratch",
                                onChange: (next) => {
                                    if (next === "scratch") {
                                        if (seedSource != null) setSetAside({ target: targetId, source: seedSource })
                                        composer.source?.onRemove()
                                        return
                                    }
                                    const from = similarFor ?? setAsideSource
                                    if (from != null) request({ target: targetId, from })
                                },
                            }
                          : undefined,
              }
            : null

    return {
        composer,
        transient,
        onResult,
        batch,
        settingEditor,
        crop: imageCrop.step,
        attach,
        request,
        review: stage,
        peek,
        onPeek: setPeekId,
        onReady,
        dropping,
        busy: composer.sending === true || creatingCard,
        creating: creatingCard,
        isCreating,
        summon,
        configurator: configuratorProps,
    }
}

/** La version courte du champ, sans pièce : trois réglages ; le reste est dans « Plus d'options ». */
const SHORT_SETTINGS: ReadonlySet<UITutorSessionSettingField> = new Set(["chapter", "level", "duration"])

/** Le configurateur : tout le formulaire ; pour une similaire, ce que son corps lit (le type, la durée, la consigne). */
const FULL_FORM_FIELDS: UIActivityGenerationField[] = [
    "scope",
    "chapter",
    "skillParts",
    "level",
    "duration",
    "instructions",
]
const SIMILAR_FORM_FIELDS: UIActivityGenerationField[] = ["level", "duration", "instructions"]
/** Une image ou un document : le chapitre que le champion y a lu se confirme (l'ancienne fenêtre), puis la similaire. */
const FILE_FORM_FIELDS: UIActivityGenerationField[] = ["chapter", "skillParts", "level", "duration", "instructions"]

/**
 * Ce qui manque à un envoi sans pièce : la règle de l'ancienne fenêtre (`isGenerationFormValid`), un chapitre OU
 * l'exercice décrit dans les instructions (cinq caractères) — un chapitre que le moteur n'exige pas reste non exigé.
 */
function sendMissing(
    draft: UIActivityGenerationDraft,
    engineMissing: UIActivityGenerationMissingField | null | undefined
): UIActivityGenerationMissingField | null {
    if (engineMissing !== "chapter") return engineMissing ?? null
    return missingStudentGenerationField(draft)
}

/** Un envoi attend la lecture d'une image ce temps au plus (une lecture dure d'ordinaire 5 à 20 s). */
export const READ_HOLD_MS = 45_000

/** Un focus demandé avant que le champ existe tient ce temps : au-delà, le champ qui naît ne le prend plus. */
const FOCUS_WAIT_MS = 1000

/** L'accusé « Envoyée à Léa » tient ce temps dans la relecture avant qu'elle se referme. */
const SENT_ACK_MS = 1500

/** La pièce d'un fichier joint : d'où il vient (un dépôt, un collage, le chat, le trombone), sa vignette s'il est une image. */
function fileSource(
    file: UIActivityFile | null | undefined,
    origin: TutorLiveFeedGenerationRequest["origin"] | null,
    chapterKept: boolean,
    onRemove: () => void
): NonNullable<TutorSessionComposer["source"]> {
    const pdf = isPdfFile(file)
    const name = file?.name ?? TEXTS.attached
    if (origin?.kind === "chat") {
        return {
            kind: "chatPhoto",
            title: TEXTS.chatPhoto(origin.at != null ? formatTime(new Date(origin.at).toISOString()) : null),
            caption: chapterKept ? TEXTS.chapterKept : undefined,
            thumbUri: pdf ? undefined : file?.uri,
            onRemove,
        }
    }
    return {
        kind: pdf ? "document" : "image",
        title: origin?.kind === "drop" ? TEXTS.dropped : origin?.kind === "paste" ? TEXTS.pastedImage : name,
        caption: origin?.kind === "drop" || origin?.kind === "paste" ? name : undefined,
        thumbUri: pdf ? undefined : file?.uri,
        onRemove,
    }
}
